import { ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useEmitter } from '@/plugins'
import { FormWrapper } from '@/wrappers'
import type { HourlyPrice, FormData, FormInfo, DataFile, FormIndex, RequestType } from '@/interfaces'

export function useHourlyPrice() {

    const baseForm: FormData = {
        customerName: null,
        projectName: null,
        projectNumber: null,
        phaseNumber: null,
        referenceCode: null,
        date: null,
        employeeIds: null,
        hourlyPrice: "45,00",
        hours: [],
        materialsData: [],
        resourcesData: [],
        notes: null,
    }

    const emitter = useEmitter();
    const route = useRoute();
    const wrapper = new FormWrapper<HourlyPrice>("regie-prijs");

    const empty = {...baseForm};

    const request = ref<RequestType<FormData>>({
        isLoading: false,
        error: null,
        response: null,
        data: baseForm
    });

    const formMaterials = ref<any[]>([]);
    const formResources = ref<any[]>([]);
    const formHours = ref<any[]>([
        { amount: null, percentage: null }
    ]);

    const file = ref<File|null>(null);
    const salesInvoice = ref<File|null>(null);
    const purchaseInvoice = ref<File|null>(null);

    const info = ref<FormInfo>();
    const id = ref<string|string[]>(route.params.id);
    const forms = ref<FormIndex<HourlyPrice>>({
        notSeen: 0,
        openAmount: 0,
        notInvoiced: 0,
        forms: []
    });

    async function toggleInvoiced(): Promise<void> {
        request.value.data.isInvoiced = !request.value.data.isInvoiced;

        await wrapper.editForm(id.value, getData()).then((data) => request.value.response = data.message)
            .catch((err) => request.value.error = err.response.data.message);

        emitter.emit("update:counters");
    }

    async function editForm(): Promise<void> {
        request.value = {...request.value , error: null , response: null , isLoading: !request.value.isLoading};

        await wrapper.editForm(id.value, getData()).then((data) => request.value.response = data.message)
            .catch((err) => request.value.error = err.response.data.message);

        emitter.emit("update:counters");
        request.value.isLoading = !request.value.isLoading;
    }

    async function addForm(): Promise<void> {
        request.value = {...request.value , error: null , response: null , isLoading: !request.value.isLoading};

        await wrapper.addForm(getData()).then((data) => request.value.response = data.message)
            .catch((err) => request.value.error = err.response.data.message);

        if (!request.value.error) {
            file.value = null;
            request.value.data = {...empty};
        }

        request.value.isLoading = !request.value.isLoading;
    }

    async function addSalesInvoice(): Promise<void> {
        request.value = {...request.value, error: null, response: null};

        file.value = salesInvoice.value;

        await wrapper.addInvoice(id.value, getData(), "sales").then((data) => request.value.response = data.message)
            .catch((err) => request.value.error = err.response.data.message);

        if (!request.value.error) {
            file.value = null;
            salesInvoice.value = null;
        }
    }

    async function addPurchaseInvoice(): Promise<void> {
        request.value = {...request.value, error: null, response: null};

        file.value = purchaseInvoice.value;

        await wrapper.addInvoice(id.value, getData(), "purchase").then((data) => request.value.response = data.message)
            .catch((err) => request.value.error = err.response.data.message);

        if (!request.value.error) {
            file.value = null;
            purchaseInvoice.value = null;
        }
    }

    async function getForm(): Promise<void> {
        request.value.isLoading = !request.value.isLoading;

        await wrapper.getForm(id.value).then((data) => {
            request.value.data = data;

            formHours.value = data.hours;
            formMaterials.value = data.materialsData;
            formResources.value = data.resourcesData;
        }).catch((err) => request.value.error = err.response.data.message);

        request.value.isLoading = !request.value.isLoading;
    }

    async function getIndex(): Promise<void> {
        request.value.isLoading = !request.value.isLoading;

        await wrapper.getFormsWithStats().then((data) => forms.value = data);

        emitter.emit("update:counters");
        request.value.isLoading = !request.value.isLoading;
    }

    async function getInfo(): Promise<void> {
        request.value.isLoading = !request.value.isLoading;

        await wrapper.getInfo().then((data) => info.value = data);

        request.value.isLoading = !request.value.isLoading;
    }

    function getData(): DataFile {
        return {
            file: file.value,
            data: JSON.stringify(request.value.data)
        }
    }

    watch([formHours, formMaterials, formResources], ([hours, materials, resources]) => {
        formHours.value = hours;
        formMaterials.value = materials;
        formResources.value = resources;

        request.value.data.hours = hours;
        request.value.data.materialsData = materials;
        request.value.data.resourcesData = resources;
    }, { immediate: true });

    return {
        file,
        info,
        forms,
        request,
        formHours,
        formMaterials,
        formResources,
        salesInvoice,
        purchaseInvoice,
        addForm,
        getForm,
        getInfo,
        getIndex,
        editForm,
        toggleInvoiced,
        addSalesInvoice,
        addPurchaseInvoice
    };
}